
import { VersionString } from "../Interface/Version.js";
import { chromeVersion, electronVersion, arch, osRelease, platform, isDev } from "../Interface/Bridge.js";
import { debugLevel } from "../Interface/JSHelpers.js";
import { join, split, concat } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { recentProblems, recentExceptions, problemReport, enabled, namesOfMask } from "../Shared/Log.js";
import { head, isEmpty, singleton as singleton_1, iterate, mapIndexed, empty, length, tryFind, ofArray } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { singleton, append, delay, toList } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { defaultArg } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { item, tryLast } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { count } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { Info_bugReportQuestions } from "../Common/AppMessages.js";
import * as react from "react";
import { keyValueList } from "../fable_modules/fable-library-js.5.13.0/MapUtil.js";
import { Option, button } from "../fable_modules/Fulma.3.0.0/Elements/Button.fs.js";
import { Common_GenericOption, Color_IColor, Size_ISize } from "../fable_modules/Fulma.3.0.0/Common.fs.js";
import { successNotification } from "./Notifications.js";
import { Msg } from "../Model/ModelType.js";
import { h4, h5 } from "../fable_modules/Fulma.3.0.0/Elements/Heading.fs.js";
import { dynamicClosablePopupFunc } from "../DrawBlock/PopupHelpers.js";
import { input, checkbox } from "../fable_modules/Fulma.3.0.0/Elements/Form/Checkbox.fs.js";
import { CSSProp, DOMAttr, HTMLAttr } from "../fable_modules/Fable.React.9.4.0/Fable.React.Props.fs.js";

export const Constants_maxBlockHeight = "20em";

export const Constants_copiedNotificationMs = 3000;

function environmentLines() {
    return ofArray([`Issie ${VersionString} (${isDev ? "development" : "installed"} build${(debugLevel() > 0) ? (`, debug level ${debugLevel()}`) : ""})`, (`${platform} ${osRelease} ${arch}, Electron ${electronVersion}, `) + concat("Chrome ", chromeVersion), concat("logging: ", namesOfMask(enabled()))]);
}

function sessionLines(model) {
    if (model != null) {
        const m = model;
        const simulator = m.SimulateInRenderer ? "renderer (development only)" : ".NET sidecar";
        let heap;
        try {
            heap = (`, heap ${~~((performance.memory.usedJSHeapSize) / 1000000)}MB of ${~~((performance.memory.jsHeapSizeLimit) / 1000000)}MB`);
        }
        catch (matchValue) {
            heap = "";
        }
        return toList(delay(() => {
            let matchValue_1, p, name, array, top, option_2;
            return append((matchValue_1 = m.CurrentProj, (matchValue_1 != null) ? ((p = matchValue_1, (name = defaultArg(tryLast((array = split(p.ProjectPath, ["/", "\\"]), array.filter((part) => (part !== "")))), "?"), (top = defaultArg((option_2 = tryFind((ldc) => ldc.IsTopSheet, p.LoadedComponents), (option_2 != null) ? option_2.Name : undefined), "none marked"), singleton(((`project '${name}', ${length(p.LoadedComponents)} sheets, top sheet '${top}', `) + concat("showing \'", p.OpenFileName, "\' ")) + (`(${count(m.Sheet.Wire.Symbol.Symbols)} symbols, ${count(m.Sheet.Wire.Wires)} wires)`)))))) : singleton("no project open")), delay(() => singleton(concat("simulating in the ", simulator, heap))));
        }));
    }
    else {
        return empty();
    }
}

/**
 * What the panel shows: everything Issie knows and nothing to fill in. The questions are on the
 * tab beside it to be read, and in the copied text to be answered - putting them here as well
 * would be the same words three times on one screen.
 */
export function viewText(model) {
    return join("\n", toList(delay(() => append(environmentLines(), delay(() => append(sessionLines(model), delay(() => append(singleton(""), delay(() => singleton(problemReport()))))))))));
}

/**
 * What the Copy button puts on the clipboard: the same, and the questions as a form.
 * 
 * **The questions travel with the report because that is where they get answered.** A checklist
 * read on one screen and typed into an email on another is a checklist half of which is
 * forgotten; one that arrives already in the email, above the evidence, is answered in place.
 * They come from `AppMessages.Info.bugReportQuestions`, the same list the tab renders.
 */
export function copyText(model) {
    const questions = join("\n", mapIndexed((i, q) => ((`${i + 1}. ${q}`) + "\n"), Info_bugReportQuestions));
    return join("\n", toList(delay(() => append(singleton("=== Issie bug report ==="), delay(() => append(singleton(""), delay(() => append(environmentLines(), delay(() => append(sessionLines(model), delay(() => append(singleton(""), delay(() => append(singleton("--- please answer these, then send the whole of this ---"), delay(() => append(singleton(""), delay(() => append(singleton(questions), delay(() => singleton(problemReport()))))))))))))))))))));
}

function summaryLine() {
    const exceptions = length(recentExceptions()) | 0;
    let problems;
    const array = recentProblems();
    problems = array.length;
    switch (exceptions) {
        case 0:
            if (problems === 0) {
                return "Nothing has gone wrong this session.";
            }
            else {
                return `${problems} error or warning message(s) this session, and no uncaught exceptions.`;
            }
        case 1:
            return `1 uncaught exception and ${problems} error or warning message(s) this session.`;
        default:
            return `${exceptions} uncaught exceptions and ${problems} error or warning message(s) this session.`;
    }
}

function textBlock(text) {
    const props = [["style", {
        whiteSpace: "pre-wrap",
        userSelect: "text",
        background: "#f5f5f5",
        border: "1px solid #dbdbdb",
        borderRadius: "4px",
        padding: "8px",
        marginTop: "8px",
        marginBottom: "8px",
        maxHeight: Constants_maxBlockHeight,
        overflowY: "auto",
    }]];
    return react.createElement("div", keyValueList(props, 1), text);
}

function copyButton(model, dispatch, label) {
    return button(ofArray([new Option(/* Size */ 1, [Size_ISize.IsSmall]), new Option(/* Color */ 0, [Color_IColor.IsInfo]), new Option(/* OnClick */ 18, [(_arg) => {
        window.issieBridge.clipboardWrite(copyText(model));
        iterate(dispatch, ofArray([new Msg(/* SetSimulationNotification */ 76, [(dispatch_1) => successNotification("Report copied - paste it into an email or a GitHub issue, and answer the questions in it", Msg.CloseSimulationNotification, dispatch_1)]), new Msg(/* DispatchDelayed */ 42, [[Constants_copiedNotificationMs, Msg.CloseSimulationNotification]])]));
    }])]), singleton_1(label));
}

/**
 * The Bug Reports tab's panel: what has gone wrong, foldable so that it does not bury the
 * checklist above it, and a button that puts the lot on the clipboard.
 * 
 * `details` rather than a model field, because whether a user has this open is not something the
 * application needs to know or to remember - and holding it in the model would mean a message,
 * an update case and a render of the whole application for a disclosure triangle.
 */
export function panel(model, dispatch) {
    let children, children_4, props_2;
    const props_6 = [["style", {
        marginTop: "1em",
    }]];
    const children_6 = [h5(empty())(singleton_1("This session")), (children = [summaryLine()], react.createElement("div", {}, ...children)), (children_4 = [(props_2 = [["style", {
        cursor: "pointer",
        marginTop: "8px",
    }]], react.createElement("summary", keyValueList(props_2, 1), "Show what will be sent")), textBlock(viewText(model))], react.createElement("details", {}, ...children_4)), copyButton(model, dispatch, "Copy error report")];
    return react.createElement("div", keyValueList(props_6, 1), ...children_6);
}

function mostRecent() {
    const matchValue = recentExceptions();
    const matchValue_1 = recentProblems();
    if (isEmpty(matchValue)) {
        if (matchValue_1.length > 0) {
            const lines_1 = matchValue_1;
            return item(lines_1.length - 1, lines_1);
        }
        else {
            return "(nothing recorded)";
        }
    }
    else {
        const latest = head(matchValue);
        return concat(latest.Source, ": ", latest.Message);
    }
}

export const popup = dynamicClosablePopupFunc("Issie logged an error", (dispatch, model) => {
    let props, children, props_2, children_2, s_1;
    const children_4 = [(props = [["style", {
        marginBottom: "8px",
    }]], (children = [mostRecent()], react.createElement("div", keyValueList(props, 1), ...children))), (props_2 = [["style", {
        fontSize: "0.85em",
        color: "#7a7a7a",
    }]], (children_2 = [(s_1 = "Issie is not meant to produce these. Everything below is also on Info > Bug Reports, and this popup is in debug builds only.", s_1)], react.createElement("div", keyValueList(props_2, 1), ...children_2))), textBlock(viewText(model)), checkbox(empty(), ofArray([input(singleton_1(new Common_GenericOption(/* Props */ 1, [ofArray([["style", {
        marginRight: "5px",
    }], new HTMLAttr(/* Checked */ 62, [model.SuppressErrorPopups]), new DOMAttr(/* OnChange */ 9, [(_arg) => {
        dispatch(new Msg(/* SetSuppressErrorPopups */ 48, [!model.SuppressErrorPopups]));
    }])])]))), "Stop showing this for the rest of this session"]))];
    return react.createElement("div", {}, ...children_4);
}, (dispatch_1, model_1) => {
    const children_6 = [copyButton(model_1, dispatch_1, "Copy error report"), button(ofArray([new Option(/* Size */ 1, [Size_ISize.IsSmall]), new Option(/* Props */ 17, [singleton_1(["style", {
        marginLeft: "8px",
    }])]), new Option(/* OnClick */ 18, [(_arg_1) => {
        dispatch_1(Msg.ClosePopup);
    }])]), singleton_1("Close"))];
    return react.createElement("div", {}, ...children_6);
}, singleton_1(new CSSProp(/* Width */ 395, [800])));

/**
 * What is drawn when drawing anything else threw.
 * 
 * Deliberately does not try to be the application: there is no dispatch it can trust, because
 * whatever the view choked on is still in the model, and a button that dispatches would run the
 * same render again. What it can do is say what happened, show it, and offer the clipboard - so
 * that a session which is over anyway still produces a usable bug report.
 */
export function crashPage(message) {
    let children, s_1, props_2;
    const props_4 = [["style", {
        padding: "2em",
        maxWidth: "60em",
    }]];
    const children_4 = [h4(empty())(singleton_1("Issie could not draw the screen")), (children = [(s_1 = "This is a bug in Issie, not in your design. Your project files have not been changed by it. Please copy the report below and send it to us, then restart Issie.", s_1)], react.createElement("div", {}, ...children)), textBlock(viewText(undefined)), button(ofArray([new Option(/* Color */ 0, [Color_IColor.IsInfo]), new Option(/* OnClick */ 18, [(_arg) => {
        window.issieBridge.clipboardWrite(copyText(undefined));
    }])]), singleton_1("Copy error report")), (props_2 = [["style", {
        marginTop: "1em",
        fontSize: "0.85em",
        color: "#7a7a7a",
    }]], react.createElement("div", keyValueList(props_2, 1), message))];
    return react.createElement("div", keyValueList(props_4, 1), ...children_4);
}

